import { z } from "zod";
import { AgentTypeMetadata } from "../../config/agent-registry";
import { baseAgentConfig } from "../../config/base-agent-config";

/**
 * API Agent Configuration Schema
 * Simplified schema for generic API connections with select-based auth method
 */
const apiConfigSchema = baseAgentConfig.extend({
  // API Base URL
  apiEndpoint: z
    .string()
    .url()
    .describe("Base API URL (e.g., https://api.example.com/v1)"),

  // Default HTTP Method (Optional)
  defaultMethod: z
    .enum(["GET", "POST", "PUT", "PATCH", "DELETE"])
    .default("GET")
    .describe("Default HTTP method for requests"),

  // Authentication Method (Select Dropdown)
  authMethod: z
    .enum(["none", "bearer", "basic", "apiKey", "oauth"])
    .default("none")
    .describe("Authentication method"),

  // Bearer Token Authentication
  bearerToken: z
    .string()
    .optional()
    .describe("Bearer token (if using Bearer auth)"),

  // Basic Authentication
  basicUsername: z
    .string()
    .optional()
    .describe("Username (if using Basic auth)"),
  basicPassword: z
    .string()
    .optional()
    .describe("Password (if using Basic auth)"),

  // API Key Authentication
  apiKeyValue: z
    .string()
    .optional()
    .describe("API key value (if using API Key auth)"),
  apiKeyLocation: z
    .enum(["header", "query"])
    .default("header")
    .describe("API key location"),
  apiKeyName: z
    .string()
    .optional()
    .describe("API key parameter name (e.g., X-API-Key, api_key)"),

  // OAuth 2.0 (placeholder for future implementation)
  oauthToken: z
    .string()
    .optional()
    .describe("OAuth access token (if using OAuth)"),

  // Optional Settings
  defaultHeaders: z
    .string()
    .optional()
    .describe('Additional headers as JSON (e.g., {"User-Agent": "MyApp"})'),

  timeoutMs: z
    .number()
    .min(1000)
    .max(300000)
    .default(30000)
    .describe("Request timeout in milliseconds"),
});

export const apiAgentMetaData: AgentTypeMetadata<typeof apiConfigSchema> = {
  name: "API",
  description:
    "Make HTTP requests to any REST API with flexible authentication and configuration",
  category: "Web",
  icon: "api",
  type: "agent",
  version: "Released",
  ribbon: "New",
  status: "Active",
  href: "/admin/ai-configuration/connectors/api",
  instructions: `You are a Generic API Agent that makes HTTP requests to REST APIs using pre-configured connections.

## IMPORTANT: Use Your Connections!
You have access to API connections that contain:
- Base URL/Endpoint (already configured)
- Authentication credentials (already configured)
- Default HTTP method
- Default headers

**DO NOT ask the user for endpoint URLs, authentication, or credentials - these are already in your connections!**

## How to Use the API Tool:
1. Check the available connections provided to you
2. Use the generic-api-call tool with:
   - connectionId: The ID of the connection to use (or leave empty for default)
   - path: The API path to call (e.g., "/rest/api/2/search", "/users/123")
   - method: HTTP method (optional, uses connection default)
   - query: Query parameters as object (optional)
   - body: Request body for POST/PUT/PATCH (optional)

## Examples:
- User: "Get all Jira tickets"
  → Use connection with Jira endpoint, path="/rest/api/2/search", method="GET"
  
- User: "Create a new user with name John"
  → Use connection, path="/users", method="POST", body={"name": "John"}

- User: "Check status of order 12345"
  → Use connection, path="/orders/12345", method="GET"

## What You Should Know:
- The connection already has the base URL (e.g., https://yourcompany.atlassian.net)
- The connection already has authentication configured
- You just need to specify the path, method, and any data

Only ask the user for:
- Which resource/data they want (e.g., "which ticket?", "what data to create?")
- NOT for URLs, authentication, or technical API details!`,

  placeholders: {
    apiEndpoint: "https://api.example.com/v1",
    defaultMethod: "GET",
    authMethod: "bearer",
    bearerToken: "your-bearer-token-here",
    basicUsername: "username",
    basicPassword: "password",
    apiKeyValue: "your-api-key",
    apiKeyLocation: "header",
    apiKeyName: "X-API-Key",
    oauthToken: "your-oauth-access-token",
    defaultHeaders: '{"User-Agent": "Junet-API-Agent"}',
    timeoutMs: "30000",
  },
  configSchema: apiConfigSchema,
  sensitiveKeys: ["bearerToken", "basicPassword", "apiKeyValue", "oauthToken"],
};
